import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useApiClient } from "@/hooks/use-api-client";
import { supabase } from "@/lib/supabase";

const HEARTBEAT_INTERVAL_MS = 120000; // 2 minutes // 2 minutes // 30 seconds
const MIN_INTERVAL_MS = 60000; // Minimum 60s between heartbeats // Minimum 60s between heartbeats // Minimum 25s between heartbeats

export function usePresenceHeartbeat() {
  const { user } = useAuth();
  const location = useLocation();
  const apiClient = useApiClient();
  const lastHeartbeatTimeRef = useRef<number>(0);
  const pathRef = useRef(location.pathname);

  useEffect(() => {
    pathRef.current = location.pathname;
  }, [location.pathname]);

  const sendHeartbeat = async (force = false) => {
    if (!user) return;
    if (typeof document !== "undefined" && document.hidden) return;
    const now = Date.now();
    if (!force && (now - lastHeartbeatTimeRef.current < MIN_INTERVAL_MS)) {
      return;
    }
    lastHeartbeatTimeRef.current = now;
    const currentPath = pathRef.current || window.location.pathname;

    try {
      if (apiClient && (apiClient as any).post) {
        await (apiClient as any).post("/api/v1/presence/heartbeat", {
          current_page: currentPath,
        });
        return;
      }
    } catch (apiErr) {}

    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (token) {
        const backendUrl = import.meta.env.VITE_BACKEND_URL || "http://localhost:8001";
        await fetch(`${backendUrl}/api/v1/presence/heartbeat`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`
          },
          body: JSON.stringify({ current_page: currentPath })
        });
      }
    } catch (fetchErr) {
      console.debug("Presence heartbeat fetch failed:", fetchErr);
    }
  };

  useEffect(() => {
    if (!user?.id) return;
    sendHeartbeat(false);

    const intervalId = setInterval(() => {
      sendHeartbeat(false);
    }, HEARTBEAT_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [user?.id]);
}
